import {
  MouseEvent,
  ReactNode,
  cloneElement,
  isValidElement,
  useState,
} from 'react';

import {
  Button,
  ButtonProps,
  Menu,
  MenuProps as MuiMenuProps,
} from '@material-ui/core';

import { MenuOption, MenuOptionProps } from './MenuOption';

export type ListOption = MenuOptionProps | JSX.Element;

function isActionButton(props: ListOption): props is MenuOptionProps {
  return (props as MenuOptionProps).label !== undefined;
}
type RenderButtonProps = {
  onClick: (event: MouseEvent) => void;
  disabled: boolean;
} & Pick<
  ButtonProps,
  'aria-expanded' | 'aria-haspopup' | 'endIcon' | 'role' | 'name'
>;

type ActionMenuProps = {
  id?: string;
  disabled?: boolean;
  buttons: ListOption[];
  renderButton: (props: RenderButtonProps, open: boolean) => ReactNode;
  MenuProps?: Partial<MuiMenuProps>;
};

export const ActionMenu = ({
  buttons = [],
  disabled = false,
  renderButton = props => <Button {...props}>Actions</Button>,
  id = 'action-menu',
  MenuProps,
}: ActionMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | Element>(null);
  const open = Boolean(anchorEl);
  const handleOpen = (event: MouseEvent<Element>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const button = renderButton(
    {
      onClick: handleOpen,
      name: `${id}-button`,
      disabled,
      role: 'button',
      'aria-haspopup': 'true',
      'aria-expanded': open ? 'true' : 'false',
    },
    open,
  );
  return (
    <>
      {button}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        getContentAnchorEl={null}
        data-testid={`${id}-menu`}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        MenuListProps={{
          'aria-labelledby': id,
        }}
        {...MenuProps}
      >
        {buttons.map(b => {
          if (isActionButton(b)) {
            return (
              <MenuOption
                {...b}
                onClick={() => {
                  handleClose();
                  b.onClick?.();
                }}
                key={`menu-option-${b.key}`}
              />
            );
          }
          if (isValidElement<{ onClick?: (e: MouseEvent) => void }>(b)) {
            const { onClick } = b.props;
            return cloneElement(b, {
              onClick: (e: MouseEvent) => {
                handleClose();
                onClick?.(e);
              },
            });
          }
          return b;
        })}
      </Menu>
    </>
  );
};
